// @flow
import React from 'react';
import type { Node } from 'react';
import styled, { type StyledComponent } from 'styled-components';

import { DSToken, MQ } from '@rover/react-lib/styles';

import BigRadioButton, { type Props as RadioButtonProps } from '../../buttons/BigRadioButton';
import Icon from '../../typography/Icon';

const V_CENTER = `
  display: flex;
  align-items: center;
`;

const Wrapper: StyledComponent<{}> = styled.label`
  margin-bottom: 0;
  ${V_CENTER} width: 100%;
  flex-direction: row;
  border: 1px solid ${DSToken.BORDER_COLOR_PRIMARY};
  padding: 15px;
  cursor: pointer;
  font-weight: normal;

  &:first-child {
    border-top-left-radius: ${DSToken.BORDER_RADIUS};
    border-top-right-radius: ${DSToken.BORDER_RADIUS};
  }

  &:last-child {
    border-bottom-left-radius: ${DSToken.BORDER_RADIUS};
    border-bottom-right-radius: ${DSToken.BORDER_RADIUS};
  }

  &:not(:first-child) {
    border-top: none;
  }

  ${MQ.XS} {
    align-items: flex-start;
  }
`;

const RadioButtonWrapper: StyledComponent<{}> = styled.div`
  width: 28px;
  margin-right: 5px;

  ${MQ.XS} {
    margin-top: 3px;
  }
`;

export const ContentWrapper: StyledComponent<{}> = styled.div`
  ${V_CENTER} flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding-right: 30px;

  ${MQ.XS} {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }
`;

const DeleteButtonWrapper: StyledComponent<{}> = styled.div`
  height: 14px;
  margin-right: 15px;
  color: ${DSToken.TEXT_COLOR_TERTIARY};

  ${MQ.XS} {
    margin-right: 0;
  }
`;

export const DeleteIcon: StyledComponent<React$ElementConfig<typeof Icon>, {}, any> = styled(Icon)`
  position: relative;
  top: -3px;
`;

const handleDeleteClicked = (event, cb) => {
  event.preventDefault();
  cb();
};

const renderDelete = (value, onDelete) => {
  if (!onDelete) return null;

  return (
    <DeleteButtonWrapper>
      <DeleteIcon
        icon="rover-icon-generic-none"
        data-qa-id="components-ff-radio-rows-buttons-delete"
        onClick={e => handleDeleteClicked(e, () => onDelete(value))}
      />
    </DeleteButtonWrapper>
  );
};

export type Props = {
  options: Array<{
    value: string,
    label?: string,
    children: Node,
  }>,
  name: string,
  radioProps: $Shape<RadioButtonProps>,
  value: string,
  onChange: string => void,
  onDelete: ?(string) => void,
};

const RadioRows = ({ options, value, name, onChange, onDelete, radioProps, ...other }: Props) => (
  <div>
    {options.map(option => (
      <Wrapper key={option.value} {...other}>
        <RadioButtonWrapper>
          <BigRadioButton
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
            {...radioProps}
          />
        </RadioButtonWrapper>
        <ContentWrapper>{option.label || option.children}</ContentWrapper>
        {renderDelete(option.value, onDelete)}
      </Wrapper>
    ))}
  </div>
);

RadioRows.defaultProps = {
  value: '',
  onDelete: undefined,
  radioProps: {},
};

export default RadioRows;



// WEBPACK FOOTER //
// ../react-lib/src/components/formFields/RadioRows/RadioRows.jsx